import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ID,
  McpCapabilityEvaluationPayload,
  McpMergedCapability,
} from '../../../../types/aiTypes';

type Props = {
  open: boolean;
  capability: McpMergedCapability | null;
  selectedServerId?: ID | null;
  onClose: () => void;
  onEvaluate: (payload: McpCapabilityEvaluationPayload) => Promise<any>;
  evaluating?: boolean;
};

type DynamicFieldType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'object'
  | 'array'
  | 'null';

type DynamicField = {
  name: string;
  label: string;
  type: DynamicFieldType;
  required: boolean;
  description?: string;
  enumValues?: any[];
  defaultValue?: any;
  placeholder?: string;
  source: 'schema' | 'argument' | 'uri_template';
};

function capabilityName(capability?: McpMergedCapability | null) {
  return capability?.capability_name || capability?.name || '';
}

function capabilityKind(capability?: McpMergedCapability | null) {
  return capability?.capability_kind || capability?.capability_type || '';
}

function titleize(value: string) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function safeJsonValue(value: any): any {
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'object') return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  return value;
}

function getSchema(capability: McpMergedCapability | null | undefined): any {
  if (!capability) return null;

  return (
    safeJsonValue(capability.input_schema) ||
    safeJsonValue((capability as any).input_schema_json) ||
    safeJsonValue(capability.raw_discovered?.input_schema) ||
    safeJsonValue((capability.raw_discovered as any)?.input_schema_json) ||
    safeJsonValue((capability.raw_discovered as any)?.raw_payload_json?.inputSchema) ||
    safeJsonValue((capability.raw_discovered as any)?.raw_payload_json?.input_schema) ||
    safeJsonValue((capability.raw_discovered as any)?.raw_payload_json?.arguments) ||
    safeJsonValue(capability.raw_discovered?.raw?.inputSchema) ||
    safeJsonValue(capability.raw_discovered?.raw?.input_schema) ||
    safeJsonValue(capability.raw_discovered?.raw?.arguments) ||
    null
  );
}

function normalizeType(raw: any): DynamicFieldType {
  if (!raw) return 'string';

  if (Array.isArray(raw)) {
    const nonNull = raw.find((item) => item !== 'null');
    return normalizeType(nonNull || raw[0] || 'string');
  }

  const value = String(raw).toLowerCase();

  if (value === 'integer') return 'integer';
  if (value === 'number') return 'number';
  if (value === 'boolean') return 'boolean';
  if (value === 'object') return 'object';
  if (value === 'array') return 'array';
  if (value === 'null') return 'null';

  return 'string';
}

function schemaType(prop: any): DynamicFieldType {
  if (!prop || typeof prop !== 'object') return 'string';

  if (prop.type) return normalizeType(prop.type);

  if (Array.isArray(prop.anyOf)) {
    const nonNull = prop.anyOf.find((item: any) => item?.type !== 'null');
    return normalizeType(nonNull?.type || 'string');
  }

  if (Array.isArray(prop.oneOf)) {
    const nonNull = prop.oneOf.find((item: any) => item?.type !== 'null');
    return normalizeType(nonNull?.type || 'string');
  }

  if (Array.isArray(prop.allOf) && prop.allOf[0]?.type) {
    return normalizeType(prop.allOf[0].type);
  }

  return 'string';
}

function schemaEnum(prop: any): any[] | undefined {
  if (!prop || typeof prop !== 'object') return undefined;

  if (Array.isArray(prop.enum)) {
    return prop.enum.filter((item: any) => item !== null);
  }

  for (const key of ['anyOf', 'oneOf']) {
    if (Array.isArray(prop[key])) {
      for (const item of prop[key]) {
        if (Array.isArray(item?.enum)) {
          return item.enum.filter((x: any) => x !== null);
        }
      }
    }
  }

  return undefined;
}

function parseSchemaFromDescription(description?: string): any {
  if (!description) return null;

  const match = description.match(/schema:\s*(\{.*\})/i);
  if (!match?.[1]) return null;

  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
}

function defaultForField(field: DynamicField): any {
  if (field.defaultValue !== undefined) return field.defaultValue;

  if (field.enumValues?.length) return field.enumValues[0];

  switch (field.type) {
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return '';
    case 'object':
      return '{}';
    case 'array':
      return '[]';
    case 'null':
      return null;
    case 'string':
    default:
      return '';
  }
}

function extractUriTemplateFields(uriTemplate?: string | null): DynamicField[] {
  if (!uriTemplate) return [];

  const names = Array.from(uriTemplate.matchAll(/\{([^}]+)\}/g))
    .map((match) => match[1])
    .filter(Boolean);

  const unique = Array.from(new Set(names));

  return unique.map((name) => ({
    name,
    label: `${titleize(name)} *`,
    type:
      name.toLowerCase().endsWith('_id') ||
      name.toLowerCase() === 'id' ||
      name.toLowerCase() === 'tag_id'
        ? 'integer'
        : 'string',
    required: true,
    description: `Value for URI template parameter {${name}}`,
    placeholder:
      name.toLowerCase() === 'entity_type'
        ? 'product'
        : name.toLowerCase().includes('id')
          ? '165'
          : '',
    source: 'uri_template',
  }));
}

function extractJsonSchemaFields(schema: any): DynamicField[] {
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return [];

  const properties = schema.properties;
  const requiredList = Array.isArray(schema.required) ? schema.required : [];

  if (!properties || typeof properties !== 'object') return [];

  return Object.entries(properties).map(([name, prop]: [string, any]) => {
    const propType = schemaType(prop);
    const enumValues = schemaEnum(prop);

    return {
      name,
      label: `${titleize(name)}${requiredList.includes(name) ? ' *' : ''}`,
      type: propType,
      required: requiredList.includes(name),
      description: prop?.description,
      enumValues,
      defaultValue: prop?.default,
      placeholder: prop?.examples?.[0] || prop?.example || '',
      source: 'schema',
    };
  });
}

function extractArgumentFields(schema: any): DynamicField[] {
  if (!schema) return [];

  const args = Array.isArray(schema)
    ? schema
    : Array.isArray(schema.arguments)
      ? schema.arguments
      : Array.isArray(schema.args)
        ? schema.args
        : [];

  if (!args.length) return [];

  return args
    .filter((arg: any) => arg && (arg.name || arg.key))
    .map((arg: any) => {
      const name = String(arg.name || arg.key);
      const required = Boolean(arg.required);
      const parsedSchema = parseSchemaFromDescription(arg.description);
      const type = normalizeType(arg.type || arg.schema?.type || parsedSchema?.type || 'string');

      return {
        name,
        label: `${titleize(name)}${required ? ' *' : ''}`,
        type,
        required,
        description: arg.description,
        enumValues: Array.isArray(arg.enum) ? arg.enum : schemaEnum(parsedSchema),
        defaultValue: arg.default,
        placeholder: arg.example || '',
        source: 'argument',
      };
    });
}

function getDynamicFields(capability: McpMergedCapability | null): DynamicField[] {
  if (!capability) return [];

  const kind = capabilityKind(capability);
  const schema = getSchema(capability);

  if (kind === 'resource') {
    const uriTemplateFields = extractUriTemplateFields(capability.uri_template);
    if (uriTemplateFields.length) return uriTemplateFields;
  }

  const schemaFields = extractJsonSchemaFields(schema);
  if (schemaFields.length) return schemaFields;

  const argumentFields = extractArgumentFields(schema);
  if (argumentFields.length) return argumentFields;

  return [];
}

function coerceValue(field: DynamicField, value: any): any {
  if (field.type === 'null') return null;

  if (field.type === 'boolean') return Boolean(value);

  if (field.type === 'integer') {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : value;
  }

  if (field.type === 'number') {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }

  if (field.type === 'object' || field.type === 'array') {
    if (value === '' || value === null || value === undefined) {
      return field.type === 'array' ? [] : {};
    }

    if (typeof value !== 'string') return value;

    return JSON.parse(value);
  }

  if (value === '' || value === null || value === undefined) {
    return field.required ? '' : undefined;
  }

  return value;
}

function safeJsonParse(value: string) {
  if (!value.trim()) return {};
  const parsed = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Raw JSON payload must be an object.');
  }
  return parsed;
}

function fillUriTemplate(uriTemplate: string, values: Record<string, any>) {
  return uriTemplate.replace(/\{([^}]+)\}/g, (_, key) => {
    const value = values[key];
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing required URI template value: ${key}`);
    }
    return encodeURIComponent(String(value));
  });
}

function buildRawSample(fields: DynamicField[]) {
  const sample: Record<string, any> = {};

  fields.forEach((field) => {
    const value = defaultForField(field);

    if (field.type === 'object') {
      try {
        sample[field.name] = JSON.parse(value || '{}');
      } catch {
        sample[field.name] = {};
      }
    } else if (field.type === 'array') {
      try {
        sample[field.name] = JSON.parse(value || '[]');
      } catch {
        sample[field.name] = [];
      }
    } else {
      sample[field.name] = value;
    }
  });

  return sample;
}

function tryParseJsonString(value: any): any {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;

  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  return value;
}

function extractTextFromMcpContent(content: any): string {
  if (!Array.isArray(content)) return '';

  return content
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item?.type === 'text' && typeof item.text === 'string') return item.text;
      if (typeof item?.text === 'string') return item.text;
      return '';
    })
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

function normalizeEvaluationResult(result: any) {
  const outerOk = result?.ok !== false;
  const evaluation = result?.result ?? result;
  const raw = evaluation?.raw;
  const structuredContent =
    raw?.structuredContent ??
    evaluation?.structuredContent ??
    undefined;

  const parsedStructured = tryParseJsonString(structuredContent);
  const data = evaluation?.data;

  let text = '';
  let parsedText: any = undefined;

  if (Array.isArray(data)) {
    text = extractTextFromMcpContent(data);
    parsedText = tryParseJsonString(text);
  } else if (typeof data === 'string') {
    text = data;
    parsedText = tryParseJsonString(data);
  }

  const effectivePayload =
    parsedStructured !== undefined
      ? parsedStructured
      : parsedText !== undefined && parsedText !== text
        ? parsedText
        : data !== undefined
          ? data
          : evaluation;

  const isMcpError =
    result?.ok === false ||
    evaluation?.isError === true ||
    raw?.isError === true ||
    parsedStructured?.ok === false ||
    parsedText?.ok === false;

  const errorMessage =
    result?.error ||
    parsedStructured?.error?.message ||
    parsedStructured?.error ||
    parsedText?.error?.message ||
    parsedText?.error ||
    raw?.error ||
    evaluation?.error ||
    '';

  return {
    ok: outerOk && !isMcpError,
    isError: Boolean(isMcpError),
    errorMessage: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
    display: effectivePayload,
    text,
    raw: result,
    normalizedPayload: result?.normalized_payload,
    salesforceTokenAvailable: result?.salesforce_token_available,
    salesforceTokenForwarded: result?.salesforce_token_forwarded,
  };
}

const JsonTree: React.FC<{ value: any; depth?: number }> = ({ value, depth = 0 }) => {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {String(value)}
      </Typography>
    );
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          Empty list
        </Typography>
      );
    }

    return (
      <Stack spacing={1}>
        {value.map((item, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 1.25, bgcolor: depth ? 'background.paper' : 'grey.50' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Item {index + 1}
            </Typography>
            <Box mt={0.5}>
              <JsonTree value={item} depth={depth + 1} />
            </Box>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          Empty object
        </Typography>
      );
    }

    return (
      <Stack spacing={1}>
        {entries.map(([key, item]) => (
          <Box key={key}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {titleize(key)}
            </Typography>
            <Box mt={0.25} pl={depth ? 1 : 0}>
              <JsonTree value={item} depth={depth + 1} />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {String(value)}
    </Typography>
  );
};

const MCPCapabilityEvaluationDialog: React.FC<Props> = ({
                                                          open,
                                                          capability,
                                                          selectedServerId,
                                                          onClose,
                                                          onEvaluate,
                                                          evaluating,
                                                        }) => {
  const name = capabilityName(capability);
  const kind = capabilityKind(capability);
  const schema = React.useMemo(() => getSchema(capability), [capability]);
  const fields = React.useMemo(() => getDynamicFields(capability), [capability]);

  const [values, setValues] = React.useState<Record<string, any>>({});
  const [forceRefresh, setForceRefresh] = React.useState(false);
  const [rawJson, setRawJson] = React.useState('{}');
  const [useRawJson, setUseRawJson] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<any>(null);
  const [schemaOpen, setSchemaOpen] = React.useState(false);
  const [rawResultOpen, setRawResultOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open || !capability) return;

    const nextFields = getDynamicFields(capability);
    const nextValues: Record<string, any> = {};

    nextFields.forEach((field) => {
      nextValues[field.name] = defaultForField(field);
    });

    setValues(nextValues);
    setForceRefresh(false);
    setUseRawJson(nextFields.length === 0 && kind !== 'resource');
    setRawJson(JSON.stringify(buildRawSample(nextFields), null, 2));
    setError(null);
    setResult(null);
    setSchemaOpen(false);
    setRawResultOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, capability?.key]);

  const setFieldValue = (fieldName: string, value: any) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const validateFields = () => {
    if (!capability) return 'No capability selected.';

    if (useRawJson) {
      safeJsonParse(rawJson);
      return null;
    }

    for (const field of fields) {
      const value = values[field.name];

      if (
        field.required &&
        (value === undefined || value === null || String(value).trim() === '')
      ) {
        return `${field.label.replace('*', '').trim()} is required.`;
      }
    }

    if (kind === 'resource' && !capability.uri_template && !useRawJson) {
      return 'Resource capability does not include a URI template. Use raw JSON payload with resource_uri.';
    }

    return null;
  };

  const buildPayloadFromFields = () => {
    const payload: Record<string, any> = {};

    for (const field of fields) {
      const rawValue = values[field.name];
      const value = coerceValue(field, rawValue);

      if (value === undefined && !field.required) continue;

      payload[field.name] = value;
    }

    if (kind === 'resource') {
      if (capability?.uri_template) {
        return {
          resource_uri: fillUriTemplate(capability.uri_template, payload),
        };
      }

      return payload;
    }

    return payload;
  };

  const buildEvaluationPayload = (): McpCapabilityEvaluationPayload => {
    const payload = useRawJson ? safeJsonParse(rawJson) : buildPayloadFromFields();

    return {
      server_id: selectedServerId || undefined,
      capability_name: name,
      capability_type: capability?.capability_type,
      capability_kind: capability?.capability_kind,
      payload,
      force_refresh: forceRefresh,
    };
  };

  const run = async () => {
    setError(null);
    setRawResultOpen(false);

    try {
      const validationError = validateFields();
      if (validationError) {
        setError(validationError);
        return;
      }

      const requestPayload = buildEvaluationPayload();
      const response = await onEvaluate(requestPayload);
      setResult(response);
    } catch (err: any) {
      setError(err?.message || 'Evaluation failed.');
    }
  };

  const renderField = (field: DynamicField) => {
    const value = values[field.name];

    if (field.enumValues?.length) {
      return (
        <TextField
          fullWidth
          select
          label={field.label}
          value={value ?? ''}
          onChange={(event) => setFieldValue(field.name, event.target.value)}
          helperText={field.description}
        >
          {field.enumValues.map((item) => (
            <MenuItem key={String(item)} value={item}>
              {String(item)}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(event) => setFieldValue(field.name, event.target.checked)}
            />
          }
          label={field.label}
        />
      );
    }

    if (field.type === 'object' || field.type === 'array') {
      return (
        <TextField
          fullWidth
          multiline
          minRows={4}
          label={field.label}
          value={value ?? (field.type === 'array' ? '[]' : '{}')}
          onChange={(event) => setFieldValue(field.name, event.target.value)}
          helperText={field.description || `${field.type} JSON`}
        />
      );
    }

    return (
      <TextField
        fullWidth
        label={field.label}
        value={value ?? ''}
        onChange={(event) => setFieldValue(field.name, event.target.value)}
        placeholder={field.placeholder}
        helperText={field.description}
        type={field.type === 'integer' || field.type === 'number' ? 'number' : 'text'}
      />
    );
  };

  const normalizedResult = result ? normalizeEvaluationResult(result) : null;

  return (
    <>
      <Dialog open={open} onClose={evaluating ? undefined : onClose} fullWidth maxWidth="lg">
        <DialogTitle>Evaluate MCP Capability</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Box
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {name || 'No capability selected'}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Kind: {kind || '-'} | Server: {selectedServerId || '-'}
                  </Typography>

                  {capability?.description ? (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      {capability.description}
                    </Typography>
                  ) : null}

                  {kind === 'resource' && capability?.uri_template ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                      sx={{ wordBreak: 'break-word' }}
                    >
                      URI Template: {capability.uri_template}
                    </Typography>
                  ) : null}
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSchemaOpen(true)}
                  disabled={!schema}
                >
                  View Schema
                </Button>
              </Stack>
            </Box>

            {error ? <Alert severity="error">{error}</Alert> : null}

            {fields.length ? (
              <Grid container spacing={2}>
                {fields.map((field) => (
                  <Grid
                    item
                    xs={12}
                    md={field.type === 'object' || field.type === 'array' ? 12 : 6}
                    key={field.name}
                  >
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            ) : !useRawJson ? (
              <Alert severity="info">
                This capability does not expose an input schema. Use raw JSON payload if the
                operation requires input.
              </Alert>
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  checked={useRawJson}
                  onChange={(event) => setUseRawJson(event.target.checked)}
                />
              }
              label="Use raw JSON payload"
            />

            {useRawJson ? (
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Raw JSON Payload"
                value={rawJson}
                onChange={(event) => setRawJson(event.target.value)}
                helperText="Payload sent exactly as JSON object to the MCP evaluator."
              />
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  checked={forceRefresh}
                  onChange={(event) => setForceRefresh(event.target.checked)}
                />
              }
              label="Force refresh"
            />

            {normalizedResult ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: normalizedResult.ok ? 'success.50' : 'error.50',
                  borderColor: normalizedResult.ok ? 'success.light' : 'error.light',
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color={normalizedResult.ok ? 'success' : 'error'}
                      label={normalizedResult.ok ? 'Success' : 'Error'}
                    />

                    {normalizedResult.salesforceTokenAvailable !== undefined ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`SF token: ${normalizedResult.salesforceTokenAvailable ? 'available' : 'missing'}`}
                      />
                    ) : null}

                    {normalizedResult.salesforceTokenForwarded !== undefined ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Forwarded: ${normalizedResult.salesforceTokenForwarded ? 'yes' : 'no'}`}
                      />
                    ) : null}
                  </Stack>

                  {normalizedResult.isError ? (
                    <Alert severity="error">
                      {normalizedResult.errorMessage || 'MCP capability returned an error.'}
                    </Alert>
                  ) : null}

                  {!normalizedResult.isError ? (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Result
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                        <JsonTree value={normalizedResult.display} />
                      </Paper>
                    </Box>
                  ) : null}

                  {normalizedResult.normalizedPayload ? (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Sent Payload
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                        <JsonTree value={normalizedResult.normalizedPayload} />
                      </Paper>
                    </Box>
                  ) : null}

                  <Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setRawResultOpen((v) => !v)}
                    >
                      {rawResultOpen ? 'Hide Raw Result' : 'View Raw Result'}
                    </Button>
                  </Box>

                  {rawResultOpen ? (
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        overflow: 'auto',
                        maxHeight: 520,
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(normalizedResult.raw, null, 2)}
                      </pre>
                    </Box>
                  ) : null}
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={evaluating}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={run}
            disabled={!!evaluating || !name}
          >
            {evaluating ? 'Evaluating...' : 'Evaluate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={schemaOpen} onClose={() => setSchemaOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Discovered Input Schema</DialogTitle>
        <DialogContent dividers>
          {schema ? (
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
                overflow: 'auto',
                maxHeight: 600,
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(schema, null, 2)}
              </pre>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No schema was discovered for this capability.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MCPCapabilityEvaluationDialog;